// src/components/Dashboard/AdminDashboard/AllUsers/AdminContractReview.js

import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import { BASE_URL } from "../../../../config"; // adjust path if needed
import "./AdminContractReview.css";

const AdminContractReview = () => {
    const [contracts, setContracts] = useState([]);
    const [failedContracts, setFailedContracts] = useState([]); // ✅ NEW: failed activations feed
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOfferByPlayer, setSelectedOfferByPlayer] = useState({});
    const [reviewOffer, setReviewOffer] = useState(null);
    const [rejectingOffer, setRejectingOffer] = useState(null);
    const [rejectReason, setRejectReason] = useState("");

    // ✅ Tabs (Pending | Failed Activation)
    const [activeTab, setActiveTab] = useState("pending"); // "pending" | "failed"

    // ✅ NEW: allow admin to choose whether to close competing offers immediately
    // Default OFF to prevent killing good offers before activation/payment happens.
    const [closeOthersByPlayer, setCloseOthersByPlayer] = useState({}); // { [playerId]: boolean }

    // ✅ Central refresh so UI always matches backend rules (including 24h hold hiding)
    const refreshPending = async () => {
        const res = await apiFetch("/api/admin/pending-contracts");
        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(text || "Failed to load pending contracts.");
        }
        const data = await res.json().catch(() => []);
        setContracts(Array.isArray(data) ? data : []);
    };

    // ✅ NEW: refresh failed activation list
    // IMPORTANT: This endpoint must exist on your backend:
    // GET /api/admin/failed-contracts
    // It should return contracts that were auto-closed because payment was not completed in time.
    const refreshFailed = async () => {
        const res = await apiFetch("/api/admin/failed-contracts");
        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(text || "Failed to load failed activations.");
        }
        const data = await res.json().catch(() => []);
        setFailedContracts(Array.isArray(data) ? data : []);
    };

    // ✅ Refresh based on active tab
    const refreshActiveTab = async () => {
        if (activeTab === "pending") return refreshPending();
        return refreshFailed();
    };

    useEffect(() => {
        const initialFetch = async () => {
            try {
                setLoading(true);
                setError("");
                // load both so tab switching is instant
                await Promise.allSettled([refreshPending(), refreshFailed()]);
            } catch (err) {
                console.error(err);
                setError("Failed to load contracts.");
            } finally {
                setLoading(false);
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // When switching tabs, refresh that tab (keeps admin view current)
        const onTabChange = async () => {
            try {
                setLoading(true);
                setError("");
                await refreshActiveTab();
            } catch (err) {
                console.error(err);
                setError("Failed to load contracts.");
            } finally {
                setLoading(false);
            }
        };

        onTabChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    /* ============================= */
    /* 🔑 GROUP BY PLAYER (PENDING ONLY) */
    /* ============================= */

    const groupedByPlayer = useMemo(() => {
        return (contracts || []).reduce((acc, contract) => {
            if (!contract.playerId) return acc;

            if (!acc[contract.playerId]) {
                acc[contract.playerId] = {
                    player: contract.PlayerDatum,
                    playerId: contract.playerId,
                    offers: [],
                };
            }

            acc[contract.playerId].offers.push(contract);
            return acc;
        }, {});
    }, [contracts]);

    const playerGroups = Object.values(groupedByPlayer);

    /* ============================= */
    /* 🔍 SEARCH */
    /* ============================= */

    const filteredPlayers = playerGroups.filter((group) => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();

        return (
            group.player?.fullName?.toLowerCase().includes(term) ||
            group.player?.username?.toLowerCase().includes(term) ||
            String(group.playerId).includes(term)
        );
    });

    // ✅ Failed list filter (search by player name/username/id OR partner)
    const filteredFailed = (failedContracts || []).filter((c) => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();

        const playerName = c?.PlayerDatum?.fullName || "";
        const playerUsername = c?.PlayerDatum?.username || "";
        const playerId = c?.playerId != null ? String(c.playerId) : "";

        const partnerName =
            c?.Agent?.fullName ||
            c?.Scout?.fullName ||
            c?.Club?.clubName ||
            "";

        return (
            playerName.toLowerCase().includes(term) ||
            playerUsername.toLowerCase().includes(term) ||
            playerId.includes(term) ||
            partnerName.toLowerCase().includes(term)
        );
    });

    // getContractDoc
    const getContractDocUrl = (rawUrl) => {
        if (!rawUrl) return null;

        // Already absolute
        if (/^https?:\/\//i.test(rawUrl)) return rawUrl;

        // Relative -> backend origin
        return `${BASE_URL}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
    };

    /* ============================= */
    /* ✅ ADMIN ACTIONS */
    /* ============================= */

    const putContractDecision = async (contractId, payload) => {
        const res = await apiFetch(`/api/admin/contract-decision/${contractId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(text || "Request failed");
        }

        // Some backends return JSON; some return empty
        try {
            return await res.json();
        } catch {
            return null;
        }
    };

    const handleApprove = async (playerId) => {
        const contractId = selectedOfferByPlayer[playerId];
        if (!contractId) return;

        const closeOthers = !!closeOthersByPlayer[playerId];

        const confirmMsg = closeOthers
            ? "Approving this offer will ALSO close all other offers for this player.\n\nContinue?"
            : "Approving this offer will mark it as approved.\n\nNOTE: With pay-after-approval, competing offers may remain until activation/payment is completed.\n\nContinue?";

        if (!window.confirm(confirmMsg)) return;

        try {
            setLoading(true);
            setError("");

            // 1) Approve selected
            await putContractDecision(contractId, { status: "approved" });

            // 2) Optionally close others (old behavior preserved as an option)
            if (closeOthers) {
                const others = (contracts || []).filter(
                    (c) => c.playerId === playerId && c.id !== contractId
                );

                for (const c of others) {
                    try {
                        await putContractDecision(c.id, {
                            status: "closed",
                            rejectionReason:
                                "This offer was closed because another offer was approved for the player.",
                        });
                    } catch (closeErr) {
                        console.error("Failed closing competing offer:", c.id, closeErr);
                    }
                }
            }

            // ✅ IMPORTANT: Always refresh from backend so 24h hold/hide is respected
            await Promise.allSettled([refreshPending(), refreshFailed()]);

            // Clear selection + keep toggle setting
            setSelectedOfferByPlayer((prev) => {
                const copy = { ...prev };
                delete copy[playerId];
                return copy;
            });
        } catch (err) {
            console.error(err);
            alert(
                err.message ||
                "Approval failed. This player may already have an approved contract."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSingle = async (contractId) => {
        if (!window.confirm("This will close the offer without selecting it.\n\nContinue?")) {
            return;
        }

        try {
            setLoading(true);
            setError("");

            await putContractDecision(contractId, {
                status: "closed",
                rejectionReason:
                    "This offer was closed because another offer was approved for the player.",
            });

            // ✅ Refresh to match backend truth
            await Promise.allSettled([refreshPending(), refreshFailed()]);

            // Clear radio selection if this offer was selected
            setSelectedOfferByPlayer((prev) => {
                const copy = { ...prev };
                Object.keys(copy).forEach((pId) => {
                    if (copy[pId] === contractId) delete copy[pId];
                });
                return copy;
            });
        } catch (err) {
            console.error(err);
            alert(err.message || "Failed to close offer.");
        } finally {
            setLoading(false);
        }
    };

    const handleRejectConfirm = async () => {
        if (!rejectReason.trim()) {
            alert("Please provide a rejection reason.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            await putContractDecision(rejectingOffer.id, {
                status: "closed",
                rejectionReason: rejectReason,
            });

            // ✅ Refresh to match backend truth
            await Promise.allSettled([refreshPending(), refreshFailed()]);

            // Reset rejection modal state
            setRejectingOffer(null);
            setRejectReason("");
        } catch (err) {
            console.error(err);
            alert(err.message || "Failed to reject offer.");
        } finally {
            setLoading(false);
        }
    };

    /* ============================= */
    /* 🧱 RENDER */
    /* ============================= */

    const pendingCount = filteredPlayers.length;
    const failedCount = filteredFailed.length;

    return (
        <div className="admin-contract-review">
            <h2 className="title">Unified Contract Review</h2>
            <p className="subtitle">
                Review competing offers per player. Only <strong>ONE</strong> contract can be approved.
            </p>

            {/* ✅ Tabs like your screenshot */}
            <div className="toggle-tabs">
                <div
                    className={`toggle-option ${activeTab === "pending" ? "active" : ""}`}
                    onClick={() => setActiveTab("pending")}
                >
                    Pending {pendingCount > 0 ? `(${pendingCount})` : ""}
                </div>

                <div
                    className={`toggle-option ${activeTab === "failed" ? "active" : ""}`}
                    onClick={() => setActiveTab("failed")}
                >
                    Failed Activation {failedCount > 0 ? `(${failedCount})` : ""}
                </div>
            </div>

            <div className="contract-search-bar">
                <input
                    type="text"
                    placeholder="Search by player name, username, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="contract-search-input"
                />
            </div>

            {loading && <p>Loading...</p>}
            {error && <p className="error">{error}</p>}

            {/* ============================= */}
            {/* ✅ PENDING TAB */}
            {/* ============================= */}
            {activeTab === "pending" && (
                <>
                    {!loading && filteredPlayers.length === 0 && (
                        <p className="placeholder-message">No pending contracts to review.</p>
                    )}

                    {filteredPlayers.map((group) => {
                        const closeOthersChecked = !!closeOthersByPlayer[group.playerId];

                        return (
                            <div className="player-contract-card" key={group.playerId}>
                                <h3>
                                    {group.player?.fullName} (@{group.player?.username})
                                </h3>

                                <p className="warning-text">
                                    Approve the best offer. You can optionally close others immediately, or keep them open
                                    until activation/payment is completed.
                                </p>

                                {/* ✅ Toggle preserved (old behavior optional) */}
                                <div style={{ margin: "10px 0", fontSize: "14px" }}>
                                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <input
                                            type="checkbox"
                                            checked={closeOthersChecked}
                                            onChange={(e) =>
                                                setCloseOthersByPlayer((prev) => ({
                                                    ...prev,
                                                    [group.playerId]: e.target.checked,
                                                }))
                                            }
                                        />
                                        Close all other offers for this player after approval
                                    </label>
                                </div>

                                <div className="offer-list">
                                    {group.offers.map((offer) => {
                                        const isSelected = selectedOfferByPlayer[group.playerId] === offer.id;

                                        const roleLabel = offer.signedByRole
                                            ? offer.signedByRole.charAt(0).toUpperCase() + offer.signedByRole.slice(1)
                                            : "Role";

                                        const partnerName =
                                            offer.Agent?.fullName ||
                                            offer.Scout?.fullName ||
                                            offer.Club?.clubName ||
                                            "Unknown";

                                        const paymentStatus = offer.paymentStatus || "—";
                                        const adminApprovalLabel = offer.adminApproval ?? "pending";

                                        return (
                                            <label
                                                key={offer.id}
                                                className={`offer-option ${isSelected ? "selected" : ""}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name={`player-${group.playerId}`}
                                                    checked={isSelected}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() =>
                                                        setSelectedOfferByPlayer((prev) => ({
                                                            ...prev,
                                                            [group.playerId]: offer.id,
                                                        }))
                                                    }
                                                />

                                                <div
                                                    className="offer-summary"
                                                    onClick={() => setReviewOffer(offer)}
                                                >
                                                    <strong>{roleLabel}</strong>: {partnerName} — {offer.contractDuration}
                                                    <br />
                                                    <small>
                                                        Submitted:{" "}
                                                        {offer.submittedAt ? new Date(offer.submittedAt).toLocaleString() : "—"}
                                                    </small>
                                                    <br />
                                                    <small>
                                                        Admin: <strong>{adminApprovalLabel}</strong> · Payment:{" "}
                                                        <strong>{paymentStatus}</strong>
                                                    </small>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="mini-reject-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleCloseSingle(offer.id);
                                                    }}
                                                >
                                                    Close
                                                </button>

                                                <button
                                                    type="button"
                                                    className="mini-reject-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setRejectingOffer(offer);
                                                        setRejectReason("");
                                                    }}
                                                >
                                                    Reject
                                                </button>
                                            </label>
                                        );
                                    })}
                                </div>

                                <div className="contract-actions">
                                    <button
                                        className="approve-btn"
                                        disabled={!selectedOfferByPlayer[group.playerId] || loading}
                                        onClick={() => handleApprove(group.playerId)}
                                    >
                                        {loading ? "Processing..." : "Approve Selected Offer"}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}

            {/* ============================= */}
            {/* ✅ FAILED ACTIVATION TAB */}
            {/* ============================= */}
            {activeTab === "failed" && (
                <>
                    {!loading && filteredFailed.length === 0 && (
                        <p className="placeholder-message">No failed activations found.</p>
                    )}

                    {(filteredFailed || []).map((c) => {
                        const roleLabel = c.signedByRole
                            ? c.signedByRole.charAt(0).toUpperCase() + c.signedByRole.slice(1)
                            : "Role";

                        const partnerName =
                            c.Agent?.fullName ||
                            c.Scout?.fullName ||
                            c.Club?.clubName ||
                            "Unknown";

                        const paymentStatus = c.paymentStatus || "—";
                        const reason = c.rejectionReason || "Payment was not completed before the activation window ended.";

                        return (
                            <div className="player-contract-card" key={c.id}>
                                <h3>
                                    {c.PlayerDatum?.fullName || "Player"} (@{c.PlayerDatum?.username || "—"})
                                </h3>

                                <p className="warning-text" style={{ marginBottom: "10px" }}>
                                    <strong style={{ marginRight: 8 }}>⚠ Activation Failed</strong>
                                    {reason}
                                </p>

                                <div className="offer-list">
                                    <div className="offer-option selected" style={{ cursor: "default" }}>
                                        <div className="offer-summary" onClick={() => setReviewOffer(c)}>
                                            <strong>{roleLabel}</strong>: {partnerName} — {c.contractDuration}
                                            <br />
                                            <small>
                                                Approved:{" "}
                                                {c.adminApprovalTime ? new Date(c.adminApprovalTime).toLocaleString() : "—"}
                                            </small>
                                            <br />
                                            <small>
                                                Status: <strong>{c.status || "—"}</strong> · Admin:{" "}
                                                <strong>{c.adminApproval || "—"}</strong> · Payment:{" "}
                                                <strong>{paymentStatus}</strong>
                                            </small>
                                        </div>

                                        <div style={{ display: "flex", gap: "8px" }}>
                                            {c.agentContractUrl ? (
                                                <a
                                                    href={getContractDocUrl(c.agentContractUrl)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="mini-reject-btn"
                                                    style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    View PDF
                                                </a>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="mini-reject-btn"
                                                    disabled
                                                    style={{ opacity: 0.6, cursor: "not-allowed" }}
                                                >
                                                    No PDF
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                className="mini-reject-btn"
                                                onClick={() => setReviewOffer(c)}
                                            >
                                                Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}

            {/* ============================= */}
            {/* 🔍 REVIEW MODAL (ROLE-AWARE) */}
            {/* ============================= */}

            {reviewOffer && (
                <div className="contract-review-modal">
                    <div className="contract-review-content">
                        <h3>Contract Details</h3>

                        <div className="contract-detail-grid">
                            <div>
                                <strong>Contract ID:</strong> {reviewOffer.id}
                            </div>
                            <div>
                                <strong>Role:</strong> {reviewOffer.signedByRole}
                            </div>

                            <div>
                                <strong>Admin Approval:</strong> {reviewOffer.adminApproval ?? "pending"}
                            </div>
                            <div>
                                <strong>Payment Status:</strong> {reviewOffer.paymentStatus || "—"}
                            </div>

                            <hr />

                            <div>
                                <strong>Player:</strong>{" "}
                                {reviewOffer.PlayerDatum?.fullName} (@{reviewOffer.PlayerDatum?.username})
                            </div>

                            {reviewOffer.signedByRole === "agent" && reviewOffer.Agent && (
                                <div>
                                    <strong>Agent:</strong> {reviewOffer.Agent.fullName} (@{reviewOffer.Agent.username})
                                </div>
                            )}

                            {reviewOffer.signedByRole === "scout" && reviewOffer.Scout && (
                                <div>
                                    <strong>Scout:</strong> {reviewOffer.Scout.fullName} (@{reviewOffer.Scout.username})
                                </div>
                            )}

                            {reviewOffer.signedByRole === "club" && reviewOffer.Club && (
                                <div>
                                    <strong>Club:</strong> {reviewOffer.Club.clubName} (@{reviewOffer.Club.username})
                                </div>
                            )}

                            <hr />

                            <div>
                                <strong>Duration:</strong> {reviewOffer.contractDuration}
                            </div>
                            <div>
                                <strong>Notes:</strong> {reviewOffer.notes || "—"}
                            </div>

                            <div>
                                <strong>Contract Document:</strong>{" "}
                                {reviewOffer.agentContractUrl ? (
                                    <a
                                        href={getContractDocUrl(reviewOffer.agentContractUrl)}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View PDF
                                    </a>
                                ) : (
                                    "—"
                                )}
                            </div>

                            {/* ✅ Helpful for failed tab */}
                            {reviewOffer.activationHoldExpiresAt && (
                                <div>
                                    <strong>Activation Hold Expires:</strong>{" "}
                                    {new Date(reviewOffer.activationHoldExpiresAt).toLocaleString()}
                                </div>
                            )}

                            {reviewOffer.rejectionReason && (
                                <div>
                                    <strong>Reason:</strong> {reviewOffer.rejectionReason}
                                </div>
                            )}
                        </div>

                        <button className="close-modal-btn" onClick={() => setReviewOffer(null)}>
                            Close
                        </button>
                    </div>
                </div>
            )}

            {rejectingOffer && (
                <div className="contract-review-modal">
                    <div className="contract-review-content">
                        <h3>Reject Offer</h3>

                        <p>
                            <strong>Player:</strong>{" "}
                            {rejectingOffer.PlayerDatum?.fullName} (@{rejectingOffer.PlayerDatum?.username})
                        </p>

                        <textarea
                            placeholder="Enter rejection reason..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={4}
                        />

                        <div className="modal-actions">
                            <button onClick={handleRejectConfirm} disabled={loading}>
                                {loading ? "Processing..." : "Confirm Reject"}
                            </button>
                            <button onClick={() => setRejectingOffer(null)} disabled={loading}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminContractReview;
















